import { api } from "@convex/_generated/api";
/**
 * AI Progress Hook - V2.5
 *
 * Tracks multi-step AI operations for real-time progress UI.
 */
import { useQuery } from "convex/react";
import { useEffect, useRef } from "react";

import { logger, PortalEvents } from "~/lib/logger";

const aiProgressGet = api.ai.progress.get;

export interface AIProgressStatus {
  /** Current step number (0 if not started) */
  step: number;
  /** Total expected steps (may be undefined) */
  totalSteps: number | undefined;
  /** Tools that have been completed */
  completedTools: string[];
  /** Tokens used so far */
  tokensUsed: number;
  /** Progress status */
  status: "in_progress" | "completed" | "aborted" | "failed" | null;
  /** Whether progress is being tracked */
  isTracking: boolean;
  /** Error message if failed */
  error: string | undefined;
}

/**
 * Hook to get progress status for a thread.
 */
export function useAIProgress(threadId: string | null): AIProgressStatus {
  const progress = useQuery(aiProgressGet, threadId ? { threadId } : "skip");

  const previousStatusReference = useRef<string | null>(null);

  // Log status changes
  useEffect(() => {
    if (
      progress?.status &&
      progress.status !== previousStatusReference.current
    ) {
      if (
        progress.status === "completed" ||
        progress.status === "failed" ||
        progress.status === "aborted"
      ) {
        logger.info(PortalEvents.AI_MESSAGE_SENT, {
          thread_id: threadId,
          status: progress.status,
          steps_completed: progress.step,
          tools_used: progress.completedTools.length,
          tokens_used: progress.tokensUsed,
          error: progress.error,
        });
      }
      previousStatusReference.current = progress.status;
    }
  }, [
    progress?.status,
    progress?.step,
    progress?.completedTools,
    progress?.tokensUsed,
    progress?.error,
    threadId,
  ]);

  if (!progress) {
    return {
      step: 0,
      totalSteps: undefined,
      completedTools: [],
      tokensUsed: 0,
      status: null,
      isTracking: false,
      error: undefined,
    };
  }

  return {
    step: progress.step,
    totalSteps: progress.totalSteps ?? undefined,
    completedTools: progress.completedTools,
    tokensUsed: progress.tokensUsed,
    status: progress.status,
    isTracking: progress.status === "in_progress",
    error: progress.error ?? undefined,
  };
}
